/**
 * Kakao OAuth Authentication Utilities
 *
 * This module provides helper functions for Kakao OAuth 2.0 integration
 * in the CTV PWA application.
 */

const KAKAO_REST_API_KEY = process.env.REACT_APP_KAKAO_REST_API_KEY || '5bd92eb7a23aa09116e90bfe236317e';
const KAKAO_JS_KEY = process.env.REACT_APP_KAKAO_JS_KEY || '5daa1c34889facea7da33d5c17e3e983';
const KAKAO_REDIRECT_URI = process.env.REACT_APP_KAKAO_REDIRECT_URI ||
  `${window.location.origin}/auth/kakao/callback`;

/**
 * Get Kakao OAuth authorization URL
 * Redirects user to Kakao login page
 *
 * @returns {string} Kakao OAuth authorization URL
 */
export const getKakaoAuthUrl = () => {
  const params = new URLSearchParams({
    client_id: KAKAO_REST_API_KEY,
    redirect_uri: KAKAO_REDIRECT_URI,
    response_type: 'code',
    // prompt: 'login', // Force login screen
  });

  return `https://kauth.kakao.com/oauth/authorize?${params.toString()}`;
};

/**
 * Initialize Kakao JavaScript SDK
 * Call this once when app loads
 *
 * @returns {Promise<boolean>} True if initialized successfully
 */
export const initKakaoSDK = () => {
  return new Promise((resolve, reject) => {
    // Check if Kakao SDK is already loaded
    if (window.Kakao) {
      if (!window.Kakao.isInitialized()) {
        try {
          window.Kakao.init(KAKAO_JS_KEY);
          console.log('[Kakao SDK] Initialized successfully');
          resolve(true);
        } catch (error) {
          console.error('[Kakao SDK] Initialization failed:', error);
          reject(error);
        }
      } else {
        console.log('[Kakao SDK] Already initialized');
        resolve(true);
      }
      return;
    }

    // Load Kakao SDK dynamically
    const script = document.createElement('script');
    script.src = 'https://developers.kakao.com/sdk/js/kakao.js';
    script.async = true;

    script.onload = () => {
      try {
        window.Kakao.init(KAKAO_JS_KEY);
        console.log('[Kakao SDK] Loaded and initialized successfully');
        resolve(true);
      } catch (error) {
        console.error('[Kakao SDK] Initialization failed after load:', error);
        reject(error);
      }
    };

    script.onerror = () => {
      const error = new Error('Failed to load Kakao SDK');
      console.error('[Kakao SDK]', error);
      reject(error);
    };

    document.body.appendChild(script);
  });
};

/**
 * Redirect to Kakao login page (OAuth flow)
 * This is the recommended approach for security
 */
export const redirectToKakaoLogin = () => {
  const authUrl = getKakaoAuthUrl();
  console.log('[Kakao Auth] Redirecting to:', authUrl);
  window.location.href = authUrl;
};

/**
 * Login with Kakao using JavaScript SDK
 * Alternative approach - less secure than OAuth flow
 *
 * @returns {Promise<object>} Kakao user info
 */
export const loginWithKakaoSDK = () => {
  return new Promise((resolve, reject) => {
    if (!window.Kakao || !window.Kakao.isInitialized()) {
      reject(new Error('Kakao SDK not initialized'));
      return;
    }

    window.Kakao.Auth.login({
      success: (authObj) => {
        console.log('[Kakao SDK] Login success:', authObj);

        // Get user info
        window.Kakao.API.request({
          url: '/v2/user/me',
          success: (userInfo) => {
            console.log('[Kakao SDK] User info retrieved:', userInfo);
            resolve({
              accessToken: authObj.access_token,
              refreshToken: authObj.refresh_token,
              userInfo,
            });
          },
          fail: (error) => {
            console.error('[Kakao SDK] Failed to get user info:', error);
            reject(error);
          },
        });
      },
      fail: (error) => {
        console.error('[Kakao SDK] Login failed:', error);
        reject(error);
      },
    });
  });
};

/**
 * Logout from Kakao (JavaScript SDK)
 *
 * @returns {Promise<void>}
 */
export const logoutFromKakao = () => {
  return new Promise((resolve, reject) => {
    if (!window.Kakao || !window.Kakao.isInitialized()) {
      resolve(); // Not logged in with Kakao
      return;
    }

    if (!window.Kakao.Auth.getAccessToken()) {
      resolve(); // No access token
      return;
    }

    window.Kakao.Auth.logout(() => {
      console.log('[Kakao SDK] Logged out successfully');
      resolve();
    });
  });
};

/**
 * Get Kakao chat URL (for support button)
 *
 * @param {string} chatId - Your Kakao open chat ID
 * @returns {string} Kakao chat URL
 */
export const getKakaoChatUrl = (chatId = 'YOUR_CHAT_ID') => {
  return `https://open.kakao.com/o/${chatId}`;
};

/**
 * Open Kakao chat in new window
 *
 * @param {string} chatId - Your Kakao open chat ID
 */
export const openKakaoChat = (chatId = 'YOUR_CHAT_ID') => {
  const chatUrl = getKakaoChatUrl(chatId);
  window.open(chatUrl, '_blank', 'noopener,noreferrer');
};

/**
 * Open Kakao Plus Friend (Channel) for 1-1 chat
 * Best for business support
 *
 * @param {string} channelId - Your Kakao Plus Friend channel ID (e.g., "_xxxxx")
 */
export const openKakaoPlusFriend = (channelId) => {
  // Try to open in Kakao app first (mobile), then fallback to web
  const appUrl = `kakaotalk://plusfriend/home/${channelId}`;
  const webUrl = `https://pf.kakao.com/${channelId}/chat`;

  // Try app URL first
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = appUrl;
  document.body.appendChild(iframe);

  // Fallback to web after 1 second if app doesn't open
  setTimeout(() => {
    document.body.removeChild(iframe);
    window.open(webUrl, '_blank', 'noopener,noreferrer');
  }, 1000);
};

/**
 * Get Kakao profile link
 *
 * @param {string} kakaoId - Kakao user ID
 * @returns {string} Kakao profile URL
 */
export const getKakaoProfileUrl = (kakaoId) => {
  return `https://story.kakao.com/${kakaoId}`;
};

export default {
  getKakaoAuthUrl,
  initKakaoSDK,
  redirectToKakaoLogin,
  loginWithKakaoSDK,
  logoutFromKakao,
  getKakaoChatUrl,
  openKakaoChat,
  openKakaoPlusFriend,
  getKakaoProfileUrl,
};
